import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ModalId,
  ModalsSliceState,
  OpenModalPayload,
} from './modals.type';

const getTriggerElementId = (element: HTMLElement): string | null => {
  if (!element) {
    return null;
  }

  if (element.tagName === 'BUTTON') {
    // A soft reference is required because the context menu is not a child of the button, it is portaled in
    return element.role === 'menuitem'
      ? element.dataset['triggerElementId'] || null
      : element.id;
  }
  return getTriggerElementId(element.parentElement as HTMLElement);
};

const normalizeOpenPayload = (
  payload: OpenModalPayload,
): {
  id: ModalId;
  disableTransition: boolean;
  options?: Record<string, unknown>;
} => {
  if (typeof payload === 'string') {
    return { id: payload, disableTransition: false };
  }
  return {
    id: payload.id,
    disableTransition: payload.disableTransition ?? false,
    options: payload.options,
  };
};

export const getInitialState = (): ModalsSliceState => ({
  current: null,
  triggerElementId: null,
  disableTransition: false,
  exitWithoutTransition: null,
  options: null,
});

export const modalsSlice = createSlice({
  name: 'modals',
  initialState: () => getInitialState(),
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalPayload>) => {
      const { id, disableTransition, options } = normalizeOpenPayload(
        action.payload,
      );
      const replacedModal =
        disableTransition && state.current ? state.current : null;
      state.current = id;
      state.disableTransition = disableTransition;
      state.exitWithoutTransition = replacedModal;
      state.triggerElementId = getTriggerElementId(
        document.activeElement as HTMLElement,
      );
      state.options = options ?? null;
    },
    closeModal: (state, action: PayloadAction<ModalId>) => {
      if (state.current === action.payload) {
        state.current = null;
        state.exitWithoutTransition = null;
      }
    },
    closeAnyModal: (state) => {
      if (state.current !== null) {
        state.current = null;
        state.exitWithoutTransition = null;
      }
    },
    clearTriggerElementId: (state) => {
      state.triggerElementId = null;
    },
  },
});

export const { openModal, closeModal, closeAnyModal, clearTriggerElementId } =
  modalsSlice.actions;
